import React from 'react'
import styled from 'styled-components'

interface Props {
  setHasReadPrecaution: (b: boolean) => void
}

const PrecautionDocument: React.FC<Props> = ({
  setHasReadPrecaution,
}: Props) => {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const div = scrollRef.current
    if (!div) {
      return
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = div

      if (scrollTop + clientHeight >= scrollHeight - 1) {
        setHasReadPrecaution(true)
      }
    }

    div.addEventListener('scroll', handleScroll)

    return () => {
      div.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <Wrapper ref={scrollRef}>
      <div className="c26 c31 doc-content">
        <h3 className="c20" id="h.ntwg68mflq6d">
          <span className="c10 c14">はじめにご確認ください</span>
        </h3>
        <p className="c20">
          <span className="c3">
            サービスの予約に進む前に、以下の重要事項についてご確認ください。（※詳細な定義は以下の利用規約本体に記載）
          </span>
        </p>
        <ul className="c5 lst-kix_isg9ocyd2nhu-0 start">
          <li className="c0 li-bullet-0">
            <span className="c10">
              本サービスは、保険適用外の自由診療です。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              ご希望の医薬品が処方されない場合や、医師の判断により処方ができない場合があります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              処方薬には国内未承認の医薬品が含まれ、国の副作用被害救済制度の対象外となります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">18歳未満の方はご利用いただけません。</span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c16 c10">
              診察方法は、医師の判断により、必要に応じてビデオ通話、チャット、録画映像の送付、または電話等の多様な情報収集手段を用いる場合があります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c16 c10">
              本サービスは、医師の診察に基づき処方されたお薬を定期的にお届けする仕組みとなっており、継続服用をご希望の場合は、安全のため前回の診察から11ヶ月以内に必ず再診察をお受けいただく必要があります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c16 c10">
              その他、お薬の変更や用量アップ、一定期間を超えた処方量をご希望される場合も必ず医師の診察が必要になります。
            </span>
          </li>
        </ul>
        <p className="c20 c22">
          <span className="c3"></span>
        </p>
        <hr />
        <p className="c20 c22">
          <span className="c3"></span>
        </p>
        <h3 className="c20" id="h.kd24czl7xagb">
          <span className="c14 c10">オンライン診療サービス ご利用規約</span>
        </h3>
        <p className="c20">
          <span className="c3">
            本サービスは、SheepMedical株式会社が運営するプラットフォーム「BIOPORT+」を通じて、提携医療機関であるChloe
            Cheongdam
            Clinic（以下、「当院」といいます）が提供します。この利用規約は、当院のオンライン診療を受診いただくにあたり、医療行為および関連事項についてご同意いただくものです。安全な診療のために非常に重要な内容が含まれますので、必ず最後までお読みください。
          </span>
        </p>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.tjqfs369vkz1">
          <span className="c16 c10">第1条（オンライン診療の特性と同意）</span>
        </h4>
        <ol className="c5 lst-kix_tx6dcq9loc1z-0 start">
          <li className="c0 li-bullet-0">
            <span className="c10">
              本診療は、公的医療保険が適用されない自由診療であり、費用は全額自己負担となります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              オンライン診療は、対面診療と比べ得られる情報が限られます。医師の判断により、症状や状態によっては対面での診察や他院への受診をお勧めする場合があることを理解します。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              処方する医薬品の種類や量は、全て診察した医師の専門的判断に基づき決定されます。患者様のご希望に沿えない場合や、処方そのものができない場合があることに同意します。
            </span>
          </li>
        </ol>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.g7f6no6ev66o">
          <span className="c16 c10">第2条（予約・診察の実施について）</span>
        </h4>
        <ol className="c5 lst-kix_pviphxb1kez7-0 start">
          <li className="c0 li-bullet-0">
            <span className="c10">
              安全上の理由から、18歳未満の方は本サービスをご利用いただけません。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              診察の品質向上や安全確保のため、当院は診察内容を録音・録画する場合がございます。一方で、患者様が当院の許可なく診察内容を録音・録画・撮影すること、またその内容を第三者に共有・公開することを固く禁じます。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3 c17">
              診察方法は、医師の判断により、必要に応じてビデオ通話、チャット、録画映像の送付、または電話等の多様な情報収集手段を用いる場合があります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10 c17">
              診察がビデオ通話形式で行われる場合、原則としてご予約いただいた時間に新s夏を行います。ご予約の時間になりましたら、事前に通知されるメールまたは本サイトのマイページより診察室へご入室ください。医師が順次対応するため、診察開始まで5～10分ほどお待ちいただく場合がございます。診察が開始されましたら医師よりお声掛けしますので、マイク・カメラが機能し、音が聞こえる状態で診察室に入室したままでお待ちください。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10 c17">診察がビデオ通話形式で行われる場合</span>
            <span className="c3">
              、予約時間から10分以上ご連絡がつかない、または所定の方法での診察開始が確認できない場合は、理由の如何を問わず、予約は自動的にキャンセルされたものとみなし、キャンセル料として1,000円が発生することに同意します。
            </span>
          </li>
        </ol>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.t97j65uiqlf6">
          <span className="c16 c10">第3条（定期配送プランと再診察）</span>
        </h4>
        <ol className="c5 lst-kix_wqyvtweb1inb-0 start">
          <li className="c0 li-bullet-0">
            <span className="c10">
              本サービスで提供される処方プランは、ユーザーから所定の方法で解約手続きがなされない限り、予め定められた周期で自動的に決済および処方薬の配送が行われる「定期配送プラン」です。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              定期配送プランにおける処方は、全て医師によるオンライン診察に基づき行われます。1回の診察に基づく処方の有効期間は、医師が医学的見地から妥当と判断した期間（最長1年間）とし、その期間を超えて自動的に処方が継続されることはありません。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              前項の処方有効期間が満了した後も処方の継続を希望する場合、ユーザーは改めて当院の指定する方法で再診察を受ける必要があります。安全な服薬継続のため、ユーザーは前回の診察日から起算して6ヶ月後から11ヶ月後までの間に、必ず再診察を申し込むものとします。再診察の結果、医師が処方の継続を医学的に妥当と判断した場合に限り、新たな処方有効期間が設定され、定期配送が継続されます。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              再診察の結果、ユーザーの健康状態や症状の変化等に応じて、医師の判断により処方される医薬品の種類・量の変更、または処方自体が中止となる場合があります。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              ユーザーは、本サービスのマイページにおける所定の手続き、または当院が指定する連絡方法により、いつでも定期配送プランの解約を申し出ることができます。ただし、次回発送予定日の10日前までに解約手続きが完了していない場合、次回の決済および配送が行われることに同意します。
            </span>
          </li>
        </ol>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.ya4h6qckuljq">
          <span className="c16 c10">
            第4条（国内未承認医薬品等に関する重要事項）
          </span>
        </h4>
        <ol className="c5 lst-kix_q5jziixx289a-0 start">
          <li className="c0 li-bullet-0">
            <span className="c3">
              当院の処方には、医師の判断と責任において、海外で承認されている医薬品（国内承認薬の適応外使用を含む。以下、「国内未承認医薬品等」という）が含まれる場合があることを理解します。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              国内未承認医薬品等を服用して重篤な副作用が生じた場合、国の「医薬品副作用被害救済制度」の対象外となるリスクを承知します。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c10">
              処方される可能性のある主な国内未承認医薬品等の一覧を、以下のリンク先で確かに確認しました。
              <br />
              <br />
              【医薬品リスト】
            </span>
            <br />
            <span className="c10">
              <a
                className="c32"
                target="_blank"
                rel="noreferrer"
                href="https://drive.google.com/file/d/1WAsx6ZBCV76sadta3KS1S-UB0mWLd_B1/view?usp=sharing"
              ></a>
            </span>
            <span className="c33">
              <a
                className="c32"
                target="_blank"
                rel="noreferrer"
                href="https://drive.google.com/file/d/1WAsx6ZBCV76sadta3KS1S-UB0mWLd_B1/view?usp=sharing"
              >
                国内未承認医薬品等に関する重要事項_処方される可能性のある主な国内未承認医薬品等の一覧について.pdf
              </a>
            </span>
          </li>
        </ol>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.jvujn0hifxzv">
          <span className="c16 c10">
            第5条（特定の医薬品（外用麻酔剤等）に関する特則）
          </span>
        </h4>
        <ol className="c5 lst-kix_u5sgfgkyrjkp-0 start">
          <li className="c0 li-bullet-0">
            <span className="c3">
              当院が処方する特定の医薬品（外用麻酔剤等をいい、以下「本剤」といいます）は、利用者ご自身の皮膚に生じる痛みを緩和する目的で、処方を受けたご本人が私的に使用（セルフケア）することを前提とします。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              利用者は、理由の如何を問わず、本剤を第三者に譲渡、販売、貸与、または塗布させてはならないものとします。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">利用者がご自身で本剤を塗布した上で、</span>
            <span className="c3 c17">
              第三者が行う何らかの行為（医療行為に該当するもの、美容目的の行為、その他これらに類する行為を含む）を受ける場合
            </span>
            <span className="c3">
              、当該行為の合法性、安全性、およびそれに伴う一切のリスクについては、利用者自らの責任で判断し、確認するものとします。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              当院は、利用者が受ける第三者による行為にいかなる形でも関与するものではなく、当該行為に起因または関連して生じる全ての損害（行為の失敗、健康被害、利用者と行為者間の紛争等を含む）について、一切の責任を負いません。利用者が本条の規定に違反した場合も同様とします。
            </span>
          </li>
        </ol>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.muuaewdogv3q">
          <span className="c16 c10">第6条（利用者の責任と免責事項）</span>
        </h4>
        <ol className="c5 lst-kix_u5sgfgkyrjkp-0 start">
          <li className="c0 li-bullet-0">
            <span className="c3">
              私は、自身の健康状態、症状、体質その他の診療に関する情報を、偽りなく正確に医師に提供します。虚偽の申告や重要な情報の提供をしなかったことに起因して生じた損害について、当院および当社は一切の責任を負わないことに同意します。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              医薬品の服用により副作用やその他異常を感じた場合は、直ちに服用を中止し、速やかに自身の判断で当院または近隣の医療機関を受診します。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              ご指定の住所へ配達した後に起きた医薬品の紛失・盗難等について、当院および当社は責任を負いかねます。
            </span>
          </li>
          <li className="c0 li-bullet-0">
            <span className="c3">
              患者様ご自身の通信環境の不具合により診療に支障が生じた場合、当院および当社は責任を負わないことに同意します。
            </span>
          </li>
        </ol>
        <p className="c20 c22">
          <span className="c3"></span>
        </p>
        <hr />
        <p className="c6 c15">
          <span className="c13"></span>
        </p>
        <h4 className="c20" id="h.muuaewdogv3q">
          <span className="c16 c10">第7条（準拠法および管轄裁判所）</span>
        </h4>
        <p className="c6 c15">
          <span className="c13">
            本同意書および本診療に関する一切の紛争については、日本法を準拠法とし、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
          </span>
        </p>
        <p className="c20 c22">
          <span className="c3"></span>
        </p>
        <hr />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  height: 220pt;
  overflow-y: scroll;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;

  box-shadow: 0 -4px 6px -4px rgba(0, 0, 0, 0.3),
    0 4px 6px -4px rgba(0, 0, 0, 0.1);

  .doc-content {
    margin: 0;
    margin-top: 10px;
  }

  .lst-kix_pviphxb1kez7-0 > li:before {
    content: '' counter(lst-ctn-kix_pviphxb1kez7-0, decimal) '. ';
  }

  .lst-kix_wqyvtweb1inb-0 > li {
    counter-increment: lst-ctn-kix_wqyvtweb1inb-0;
  }

  .lst-kix_isg9ocyd2nhu-0 > li:before {
    content: '●';
  }

  ol.lst-kix_u5sgfgkyrjkp-0.start {
    counter-reset: lst-ctn-kix_u5sgfgkyrjkp-0 0;
  }

  ol.lst-kix_q5jziixx289a-0.start {
    counter-reset: lst-ctn-kix_q5jziixx289a-0 0;
  }

  ol.lst-kix_pviphxb1kez7-0 {
    list-style-type: none;
  }

  ol.lst-kix_tx6dcq9loc1z-0 {
    list-style-type: none;
  }

  .lst-kix_tx6dcq9loc1z-0 > li {
    counter-increment: lst-ctn-kix_tx6dcq9loc1z-0;
  }

  .lst-kix_tx6dcq9loc1z-0 > li:before {
    content: '' counter(lst-ctn-kix_tx6dcq9loc1z-0, decimal) '. ';
  }

  .lst-kix_u5sgfgkyrjkp-0 > li:before {
    content: '' counter(lst-ctn-kix_u5sgfgkyrjkp-0, decimal) '. ';
  }

  ol.lst-kix_wqyvtweb1inb-0 {
    list-style-type: none;
  }

  .lst-kix_q5jziixx289a-0 > li:before {
    content: '' counter(lst-ctn-kix_q5jziixx289a-0, decimal) '. ';
  }

  ol.lst-kix_u5sgfgkyrjkp-0 {
    list-style-type: none;
  }

  li.li-bullet-0:before {
    margin-left: -18pt;
    white-space: nowrap;
    display: inline-block;
    min-width: 18pt;
  }

  ol.lst-kix_pviphxb1kez7-0.start {
    counter-reset: lst-ctn-kix_pviphxb1kez7-0 0;
  }

  ol.lst-kix_q5jziixx289a-0 {
    list-style-type: none;
  }

  ol.lst-kix_wqyvtweb1inb-0.start {
    counter-reset: lst-ctn-kix_wqyvtweb1inb-0 0;
  }

  .lst-kix_wqyvtweb1inb-0 > li:before {
    content: '' counter(lst-ctn-kix_wqyvtweb1inb-0, decimal) '. ';
  }

  ul.lst-kix_isg9ocyd2nhu-0 {
    list-style-type: none;
  }

  ol.lst-kix_tx6dcq9loc1z-0.start {
    counter-reset: lst-ctn-kix_tx6dcq9loc1z-0 0;
  }

  .lst-kix_u5sgfgkyrjkp-0 > li {
    counter-increment: lst-ctn-kix_u5sgfgkyrjkp-0;
  }

  .lst-kix_pviphxb1kez7-0 > li {
    counter-increment: lst-ctn-kix_pviphxb1kez7-0;
  }

  .lst-kix_q5jziixx289a-0 > li {
    counter-increment: lst-ctn-kix_q5jziixx289a-0;
  }

  .c0 {
    border-right-style: solid;
    padding-top: 0pt;
    border-top-width: 0pt;
    border-right-width: 0pt;
    padding-left: 0pt;
    padding-bottom: 0pt;
    line-height: 1.15;
    border-left-width: 0pt;
    border-top-style: solid;
    margin-left: 36pt;
    border-left-style: solid;
    border-bottom-width: 0pt;
    border-bottom-style: solid;
    orphans: 2;
    widows: 2;
    text-align: left;
    padding-right: 0pt;
  }

  .c20 {
    border-right-style: solid;
    padding-top: 0pt;
    border-top-width: 0pt;
    border-right-width: 0pt;
    padding-left: 0pt;
    padding-bottom: 0pt;
    line-height: 1.15;
    border-left-width: 0pt;
    border-top-style: solid;
    border-left-style: solid;
    border-bottom-width: 0pt;
    border-bottom-style: solid;
    orphans: 2;
    widows: 2;
    text-align: left;
    padding-right: 0pt;
  }

  .c3 {
    color: #1b1c1d;
    font-weight: 400;
    text-decoration: none;
    vertical-align: baseline;
    font-size: 11pt;
    font-family: 'Arial';
    font-style: normal;
  }

  .c16 {
    font-weight: 700;
    text-decoration: none;
    vertical-align: baseline;
    font-size: 11pt;
    font-family: 'Arial';
    font-style: normal;
  }

  .c14 {
    font-weight: 700;
    text-decoration: none;
    vertical-align: baseline;
    font-size: 13pt;
    font-family: 'Arial';
    font-style: normal;
  }

  .c33 {
    text-decoration-skip-ink: none;
    -webkit-text-decoration-skip: none;
    color: #0b57d0;
    text-decoration: underline;
  }

  .c32 {
    color: inherit;
    text-decoration: inherit;
  }

  .c31 {
    padding-left: 10pt;
    padding-right: 10pt;
  }

  .c5 {
    padding: 0;
    margin: 0;
  }

  .c26 {
    background-color: #ffffff;
  }

  .c17 {
    font-weight: 700;
  }

  .c23 {
    height: 14pt;
  }

  .c22 {
    height: 11pt;
  }

  .c10 {
    color: #1b1c1d;
  }

  .c6 {
    border-right-style: solid;
    padding-top: 0pt;
    border-top-width: 0pt;
    border-right-width: 0pt;
    padding-left: 0pt;
    padding-bottom: 0pt;
    line-height: 1.15;
    border-left-width: 0pt;
    border-top-style: solid;
    border-left-style: solid;
    border-bottom-width: 0pt;
    border-bottom-style: solid;
    orphans: 2;
    widows: 2;
    text-align: left;
    padding-right: 0pt;
  }

  .c15 {
    height: 11pt;
  }

  .c13 {
    color: #1b1c1d;
    font-weight: 400;
    text-decoration: none;
    vertical-align: baseline;
    font-size: 11pt;
    font-family: 'Arial';
    font-style: normal;
  }

  li {
    color: #000000;
    font-size: 11pt;
    font-family: 'Arial';
  }

  p {
    color: #000000;
    font-size: 11pt;
    font-family: 'Arial';
  }

  h1 {
    padding-top: 20pt;
    color: #000000;
    font-size: 20pt;
    padding-bottom: 6pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    orphans: 2;
    widows: 2;
    text-align: left;
  }

  h2 {
    padding-top: 18pt;
    color: #000000;
    font-size: 16pt;
    padding-bottom: 6pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    orphans: 2;
    widows: 2;
    text-align: left;
  }

  h3 {
    padding-top: 16pt;
    color: #434343;
    font-size: 14pt;
    padding-bottom: 4pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    orphans: 2;
    widows: 2;
    text-align: left;
  }

  h4 {
    padding-top: 14pt;
    color: #666666;
    font-size: 12pt;
    padding-bottom: 4pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    orphans: 2;
    widows: 2;
    text-align: left;
  }

  h5 {
    padding-top: 12pt;
    color: #666666;
    font-size: 11pt;
    padding-bottom: 4pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    orphans: 2;
    widows: 2;
    text-align: left;
  }

  h6 {
    padding-top: 12pt;
    color: #666666;
    font-size: 11pt;
    padding-bottom: 4pt;
    font-family: 'Arial';
    line-height: 1.15;
    page-break-after: avoid;
    font-style: italic;
    orphans: 2;
    widows: 2;
    text-align: left;
  }
`

export default PrecautionDocument
